import { z } from "zod";
import { pagedResponse, paginationQuerySchema } from "../common/pagination.js";
const VISION_STATUS = z.enum(["queued", "processing", "completed", "failed", "cancelled"]);
const VISION_TYPE = z.enum([
    "shelf_segmentation",
    "product_detection",
    "price_detection",
    "promo_detection",
    "planogram_check",
    "availability_check",
    "share_of_shelf",
    "variant_matching",
    "combined",
]);
// Mirrored from capture-sessions/SESSION_TYPE so the Vision Runs UI can show
// the audit kind (Full Store Audit / Category Audit / OOS check / …) that
// drove the run. Keep in sync if the canonical enum gains members.
const VISION_SESSION_TYPE = z.enum([
    "full_store_audit",
    "category_audit",
    "fixture_audit",
    "planogram_check",
    "oos_check",
    "price_check",
    "promo_check",
    "recapture",
    "demo",
    "other",
]);
export const analyzeEmbeddingType = z.enum(["image", "fused"]);
export const visionRunInfo = z.object({
    id: z.string().uuid(),
    captureId: z.string().uuid(),
    runType: VISION_TYPE,
    /**
     * The capture-session's audit kind that drove this run (Full Store Audit /
     * Category Audit / OOS check / …). Surfaced on the Vision Runs table so
     * each row reads as "what kind of scan was this part of" instead of just
     * "Combined" (the internal run_type). Joined from capture_sessions.
     */
    sessionType: VISION_SESSION_TYPE,
    status: VISION_STATUS,
    modelName: z.string().nullable(),
    modelVersion: z.string().nullable(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    /**
     * Time vision actually spent processing, in ms — the sum of its self-reported
     * stage timings (`response.timings_ms`). Excludes queue wait, unlike
     * completedAt − startedAt. Null when the response carries no `timings_ms`.
     */
    visionMs: z.number().nullable(),
    errorMessage: z.string().nullable(),
    metadata: z.record(z.unknown()),
    createdAt: z.string().datetime(),
});
/**
 * Single-row detail with the full upstream response payload plus counts of
 * the rows derived from it (detections + shelf_sections). Used by the per-run
 * detail page.
 */
export const visionRunDetail = visionRunInfo.extend({
    sessionId: z.string().uuid(),
    response: z.record(z.unknown()).nullable(),
    errorJson: z.record(z.unknown()).nullable(),
    detectionsCount: z.number().int(),
    shelfSectionsCount: z.number().int(),
});
export const analyzeCaptureResponse = z.object({
    ok: z.literal(true),
    runId: z.string().uuid(),
    captureId: z.string().uuid(),
});
export const analyzeCaptureRequest = z
    .object({
    embeddingType: analyzeEmbeddingType.default("image"),
})
    .default({});
/**
 * Body shape for the frontend-driven ingest endpoint. The browser runs the
 * FastAPI vision-inference pipeline directly, then POSTs the response here
 * so the backend can persist detections + shelf_sections and feed the Task
 * Analytics page. `response` is the raw orchestrated_analysis (analyze_image_v2)
 * payload — kept loose since the upstream schema evolves.
 */
export const ingestVisionResponseRequest = z.object({
    modelName: z.string().max(120).optional(),
    modelVersion: z.string().max(40).optional(),
    response: z.record(z.unknown()),
});
export const ingestVisionResponseResponse = z.object({
    ok: z.literal(true),
    runId: z.string().uuid(),
    captureId: z.string().uuid(),
    detectionsInserted: z.number().int(),
    shelfSectionsInserted: z.number().int(),
});
export const listVisionRunsQuery = paginationQuerySchema.extend({
    status: VISION_STATUS.optional(),
    runType: VISION_TYPE.optional(),
    /** Filter to runs whose capture belongs to this capture-session public id. */
    sessionId: z.string().uuid().optional(),
});
export const visionRunsListResponse = pagedResponse(visionRunInfo);
//# sourceMappingURL=vision.schemas.js.map